import i18next from 'i18next'
import React from 'react'
import { useInView } from '../../../utils/hooks/hooks/useInView'
import { Image, ImageBase, ImageOptions } from '../../../utils/images'
import { mediaBlockClasses } from '../../../utils/style'
import { getClassName } from '../../../utils/utils'
import { Video } from '../../components/video'
import { CookiePlaceholderLayout } from '../../cookiebar/cookiebarLayout'

export type MediaBlock = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: MediaItem[]
  inview?: InviewOptions
}
export type MediaItem = {
  kind: 'image' | 'video'
  display: string
  image?: ImageBase & ImageOptions
  video?: {
    url: string
    id: string
    type: string
  }
  caption?: string
  author?: string
  source?: string
}

export type InviewOptions = {
  threshold?: number
  triggerOnce?: boolean
}

export type MediaBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  figureContentWidthClassName: string
  figureFullWidthClassName: string
  figureImageClassName: string
  figureVideoClassName: string
  mediaClassName: string
  imageClassName: string
  imageNonExpandableClassName: string
  pictureClassName: string
  figCaptionClassName: string
  captionClassName: string
  sourceClassName: string
  cookiesClassName: string
  cookiesInnerClassName: string
  cookiesTextClassName: string
  cookiesClickableClassName: string
}>

export const MediaBlockLayout = (
  props: MediaBlock & {
    videoCookieAccepted: boolean
    acceptVideoCookie: () => void
  }
) => {
  const { inview, index, title, intro, items, videoCookieAccepted, acceptVideoCookie } = props
  const classes = mediaBlockClasses
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("MediaBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')
  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(mediaBlockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(mediaBlockClasses, 'headerClassName', 'container text-center')}>
            {title && (
              <h2 className={getClassName(mediaBlockClasses, 'titleClassName', '3')} id={`${index}`}>
                {title}
              </h2>
            )}
            {intro && <p className={getClassName(mediaBlockClasses, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {items && items.length > 0 && (
          <div className={getClassName(mediaBlockClasses, 'contentClassName', 'content')}>
            {items.map((item: MediaItem, itemIndex: number) => (
              <MediaItemLayout
                key={itemIndex}
                item={item}
                title={title}
                blockIndex={index}
                itemIndex={itemIndex}
                videoCookieAccepted={videoCookieAccepted}
                acceptVideoCookie={acceptVideoCookie}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

const MediaItemLayout = (props: {
  item: MediaItem
  title: React.ReactNode
  blockIndex: number
  videoCookieAccepted: boolean
  acceptVideoCookie: () => void
  itemIndex: number
}) => {
  const { item, title, blockIndex, videoCookieAccepted, acceptVideoCookie, itemIndex } = props
  return (
    <figure
      key={`media-${blockIndex}-${itemIndex}`}
      className={
        getClassName(
          mediaBlockClasses,
          item.display == 'content-width' ? 'figureContentWidthClassName' : 'figureFullWidthClassName',
          `${item.display == 'content-width' ? 'container' : 'container-fluid'}`
        ) +
        ' ' +
        getClassName(mediaBlockClasses, item.kind === 'image' ? 'figureImageClassName' : 'figureVideoClassName', 'mb-0')
      }
    >
      {item.kind === 'image'
        ? item.image && (
          <div className={getClassName(mediaBlockClasses, 'mediaClassName', '')}>
            <Image
              {...item.image}
              classNames={{
                image: getClassName(mediaBlockClasses, (item.image?.keepOriginalSize ? 'imageNonExpandableClassName' : 'imageClassName'), 'w-100'),
                picture: getClassName(mediaBlockClasses, 'pictureClassName', ''),
              }}
            />
          </div>
        )
        : item.kind === 'video' &&
        item.video !== undefined && (
          <div className={getClassName(mediaBlockClasses, 'mediaClassName', 'text-center')}>
            {!videoCookieAccepted
              ? (<CookiePlaceholderLayout onAccept={acceptVideoCookie} />)
              : <Video
                url={item?.video?.url}
                videoCookieAccepted={videoCookieAccepted}
                acceptVideoCookie={acceptVideoCookie}
                className="figure__video"
              />
            }
          </div>
        )}

      {(item.caption || item.author) && (
        <figcaption className={getClassName(mediaBlockClasses, 'figCaptionClassName', 'mt-2')}>
          {item.caption && (
            <p className={getClassName(mediaBlockClasses, 'captionClassName', 'small text-secondary text-center m-0')}>
              {item.caption}
            </p>
          )}
          {item.author && (
            <p className={getClassName(mediaBlockClasses, 'sourceClassName', 'small text-secondary text-center m-0')}>
              {i18next.t("media:by_author", { author: item.author })}
            </p>
          )}
        </figcaption>
      )}
    </figure>
  )
}
